'use client';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useT, useSite } from '../../lib/i18n';
import { pickName } from '../../lib/i18n-core';
import { fmtDateDot } from '../../lib/datetime';
import { isMobileDevice } from '../../lib/device';
import { clock, hoursMinutes, type Classroom, type ClassroomLecture } from '../../lib/myclass-shared';
import { openPlayer, playerHref, PROGRESS_PING } from '../player/player-shared';
import { badgeClass, typeLabel } from '../course/labels';
import { limitParts } from '../course/price';
import { Icon } from '../Icon';
import { SmartLink } from '../SmartLink';
import { Working } from '../Working';
import { toast } from '../Toast';
import { AskModal } from './AskModal';

const ICON_PLAY_CIRCLE = 'circle-play';
const ICON_CLOCK = 'clock';
const ICON_TIMER = 'timer';
const ICON_CHART = 'chart-column';
const ICON_STAR = 'star';
const ICON_PEN = 'pen-line';
const ICON_FILE = 'file-text';
const ICON_SEARCH = 'search';
const ICON_GRID = 'layout-grid';
const ICON_BOOKMARK = 'bookmark';
const ICON_MSG = 'message-circle';
const ICON_CHEVRON = 'chevron-down';
const ICON_CHEVRON_UP = 'chevron-up';
const ICON_PLAY = 'play';
const ICON_DOWNLOAD = 'download';
const ICON_CHECK_SQ = 'square-check';
const ICON_INFO = 'info';

/** 기기 판정은 이 브라우저만 아는 값이다 — 바뀔 일이 없어 구독하지 않는다 */
const NO_DEVICE_SUB = () => () => {};

/**
 * 이 기기로는 못 보는 강좌인가 — 값이 있으면 **어느 쪽 전용**인지다(없으면 볼 수 있다).
 * 단가를 PC·모바일로 나눠 파는 마켓에서는 산 단가(`platform`)가 곧 수강 가능 기기다.
 * **한 곳에 둔다** — 버튼을 감추는 자리와 스크롤을 멈추는 자리가 같은 판단을 봐야 한다.
 */
const blockedDevice = (platform: Classroom['platform'], mobile: boolean): 'PC' | 'MOBILE' | null =>
  (platform === 'PC' && mobile ? 'PC' : platform === 'MOBILE' && !mobile ? 'MOBILE' : null);

/** 상태 표기 — 수강중·대기·일시정지·종료 */
const STATUS_KEY: Record<string, string> = { IN_PROGRESS: 'mc.inProgress', WAITING: 'mc.waiting', PAUSED: 'mc.paused', EXPIRED: 'mc.expired' };

/**
 * 강의실 상세 — 가이드 `myclass_info.html`(현행 `Myclass::study`).
 *
 *  · 머리 상자 — 상태·총 차시·분류 뎁스·제목·통계 4칸·강사·간략소개·[즐겨찾기][수강후기][수강확인서]
 *  · 툴바 — 강사 / 수강 여부 / 강의명 검색. 가이드의 [단계 선택] 은 뺐다(대응하는 값이 없다).
 *  · 구성 강좌 아코디언 — 정보행은 **강사 줄만**(교재·공지·Q&A 최신 글은 2026-09-10 에 뺐다).
 *    머리의 북마크(`bm-btn--group`)는 **표시**(`ClassDetail.bookmark`)다.
 *    [선택 강좌만 보기] 는 표시한 것만, [전체 보기] 는 전부. 표시하지 않은 강좌를 흐리게 하지 않는다
 *    (2026-09-07 사용자 지시 — 감추는 게 아니라 골라 모아 보는 것이다).
 *  · 차시 줄 — 체크(묶어 받기)·순서·강의명(CC)·진도·교안·[스트리밍][다운로드]·최근 수강.
 *    [스트리밍] 은 플레이어(`/player/…`, 제 창)로 간다. [다운로드] 는 PC 다운로더(현행 `pop_downloader`, 에이전트 설치형)라 아직 준비 중.
 *
 * 데이터는 한 번 받고 거르기는 전부 화면에서 한다 — 차시 수백 줄이 상한이다.
 */
/**
 * 이 강의실을 **어디로 열었나** — 나의 강의실(수강권) / 강좌 상세의 [수강하기] 탭(구독).
 * 조회 주소와 **쓸 수 있는 기능**이 여기서 갈린다.
 */
export type ClassroomSource =
  | { kind: 'enrollment'; cid: number; forceStart?: boolean }
  /** 구독 — 수강권이 없어 즐겨찾기·표시·수강확인서를 남길 자리가 없다 */
  | { kind: 'billing'; productId: number };

export function ClassroomDetail({ source }: { source: ClassroomSource }) {
  const t = useT();
  const { lang, pcApp } = useSite();
  /** Shell 앱 미설치 안내 — 공용 파일(`player-shared`)은 번역 훅을 못 써서 화면이 문구를 넘긴다 */
  const shellTexts = { notInstalled: t('pl.shell.notInstalled') };
  const [data, setData] = useState<Classroom | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [teacher, setTeacher] = useState<number | ''>('');
  const [status, setStatus] = useState<'' | 'done' | 'todo'>('');
  const [q, setQ] = useState('');
  const [selectedOnly, setSelectedOnly] = useState(false);
  /**
   * 펼친 구성 강좌 — **한 번에 하나**다(2026-09-10 사용자 지시). 여럿을 열어 두면 차시가 수백 줄로
   * 쌓여 지금 보는 강좌가 어디였는지 잃는다. 다른 강좌를 열면 앞엣것은 접힌다.
   */
  const [open, setOpen] = useState<number | null>(null);
  /**
   * 지금 **접히는 중**인 강좌 — 접힘도 부드럽게 하려면 본문이 DOM 에 남아 있어야 한다
   * (2026-09-10 사용자 지시). 다 접히면 걷어 낸다 — 차시 수백 줄을 접힌 채로 계속 들고
   * 있으면 그리기 비용만 든다. 그래서 **한 번에 많아야 둘**(열린 것 + 접히는 것)이다.
   */
  const [closing, setClosing] = useState<number | null>(null);
  /** 묶어 받기로 고른 차시 — `단과:차시`(`key()`)로 담는다. 차시 번호만으로는 같은 차시가 여러 강좌에 걸릴 때 갈리지 않는다 */
  const [checked, setChecked] = useState<Set<string>>(new Set());
  /** 분류가 여럿일 때 — 첫 줄만 보이고 펼치면 전부(2026-09-07 사용자 지시) */
  const [catsOpen, setCatsOpen] = useState(false);
  /** 이 화면에서 [스트리밍]을 눌러 최근 본 강의가 된 차시 — 서버 값(`recentCcid`)보다 앞선다 */
  const [touchedCcid, setTouchedCcid] = useState<number | null>(null);
  /**
   * 질문하기 레이어 — 열려 있으면 `{ lecture }`. 강사 줄에서 열면 그 강좌가 실려 오고,
   * 머리 상자에서 열면 비어 있다(최근 본 강좌부터 시작).
   */
  const [asking, setAsking] = useState<{ lecture?: ClassroomLecture } | null>(null);
  /**
   * 다시 읽기 신호(2026-09-17 사용자 요청) — 재생 창에서 진도가 움직이면 **진도 막대와 시청시간**을 갱신한다.
   * 진도는 번들이 서버로 직접 보내므로 이 화면은 그 일을 알 길이 없다 — 재생 창이 알려 주거나(창을 닫을 때·차시를 바꿀 때),
   * 회원이 이 화면으로 돌아왔을 때 다시 읽는다. 값을 세지 않고 **번호만 올려** 아래 읽기 효과를 다시 돌린다.
   */
  const [reload, setReload] = useState(0);

  /**
   * 이 브라우저가 휴대폰인가 — 서버 렌더에는 없는 값이라 **외부 저장소처럼** 읽는다(첫 그림과 어긋나지 않게).
   * 판정 규칙은 `lib/device.ts` 한 곳이다 — 아이패드는 데스크톱 UA 를 보내 UA 만으로는 못 가린다.
   */
  const isMobile = useSyncExternalStore(NO_DEVICE_SUB, isMobileDevice, () => false);

  /*
   * 구독은 **남길 자리가 없다**(2026-09-09) — 표시(북마크)는 `tb_class_detail` 에 붙은 칸이고
   * 구독에는 그 행이 없다. 수강확인서는 주문이 근거다. 버튼을 감춘다 — 눌러도 안 되는 버튼을
   * 두지 않는다.
   *
   * **즐겨찾기는 예외**다(2026-09-10 사용자 지시) — 별도 표(`tb_billing_favorite`)를 만들어
   * 구독으로 듣는 강좌도 담을 수 있고, 나의 강의실 즐겨찾기 탭에 함께 뜬다.
   */
  const canMark = source.kind === 'enrollment';
  const url = source.kind === 'enrollment'
    ? `/api/members/me/enrollments/${source.cid}${source.forceStart ? '?start=1' : ''}`
    : `/api/billing/classroom/${source.productId}`;

  useEffect(() => {
    let alive = true;
    fetch(url)
      .then(async (r) => {
        if (!alive) return;
        if (r.ok) {
          const d = (await r.json()) as Classroom;
          setData(d);
          /*
           * 처음 펼쳐 둘 강좌 — **최근 수강한 차시가 든 강좌**(2026-09-10 사용자 지시).
           * 패키지·패스는 구성 강좌가 여럿이라 첫째를 펴 두면 듣던 데를 찾아 다시 눌러야 한다.
           * 들은 기록이 없으면 옛 규칙대로 열린 것 중 첫째다(오픈 예정 강좌는 펼칠 수 없다).
           */
          const openable = d.lectures.filter((l) => l.state !== 'SCHEDULED');
          let last: { pid: number; at: number } | null = null;
          for (const l of openable) for (const c of l.curriculum) {
            if (!c.lastStudiedAt) continue;
            const at = new Date(c.lastStudiedAt).getTime();
            if (!last || at > last.at) last = { pid: l.productId, at };
          }
          /*
           * **처음 읽을 때만** 펼친다(2026-09-17) — 다시 읽을 때도 열면 보고 있던 강좌가 접히고 딴 데가 열린다.
           */
          if (!opened.current) {
            opened.current = true;
            setOpen(last?.pid ?? openable[0]?.productId ?? null);
          }
          return;
        }
        const body = (await r.json().catch(() => ({}))) as { message?: string };
        setError(body.message ?? t('common.loadFailed'));
      })
      .catch(() => { if (alive) setError(t('acc.err.network')); });
    return () => { alive = false; };
  }, [url, t, reload]);

  /*
   * **재생 창이 알려 주면 다시 읽는다**(2026-09-17 사용자 요청 — 진도 막대와 시청시간이 따라 움직이게).
   *
   * **PC 와 휴대폰이 서로 다른 길로 온다** — 둘 다 받는다(2026-09-17 사용자 확인 "PC·모바일 모두 됐으면").
   *  · **PC** — 재생이 제 창에서 뜬다. 그 창이 닫히거나 차시를 바꿀 때 신호가 온다(그때가 `end_content` 직후다).
   *    **같은 출처에서 온 것만** 받는다 — 다른 창이 아무 말이나 보내도 듣지 않는다.
   *  · **휴대폰** — 재생이 **같은 탭**에서 열려 이 화면은 떠난다(신호를 보낼 상대가 없다). 대신 **돌아올 때** 잡는다 —
   *    앱에서 브라우저로 넘어오며 화면이 다시 보이거나(`visibilitychange`), 뒤로 가기로 **되살아난다**(`pageshow.persisted`).
   *    되살아난 화면은 브라우저가 통째로 기억해 둔 것이라 그냥 두면 **옛 진도가 그대로 보인다** — 그래서 따로 받는다.
   */
  useEffect(() => {
    /*
     * **한 번 더 늦게 읽는다**(2026-09-17 사용자 지적 "애매한 구멍이 있어 보인다" — 맞다, 타이밍이었다).
     *
     * 우리가 아는 것은 "콜백이 나갈 **무렵**" 이지 "서버가 저장을 **마친 뒤**" 가 아니다. 둘이 겹친다 —
     *  · PC: 창을 닫는 그 순간 번들도 `end_content` 를 보낸다. 우리 조회가 먼저 닿으면 옛 값을 읽는다
     *  · 폰: 앱은 재생을 **끊는 시점**에 보낸다. 브라우저로 먼저 돌아와 있으면 아직 안 왔을 수 있다
     * 그래서 신호마다 **지금 한 번, 4초 뒤 한 번** 읽는다. 놓치면 다음 기회가 없는 자리라 값싼 보험이다.
     * 탭을 자주 오가는 사람에게 되풀이하지 않도록 2초 안의 신호는 흘린다.
     */
    let last = 0;
    let later: ReturnType<typeof setTimeout> | undefined;
    const again = () => {
      const now = Date.now();
      if (now - last < 2000) return;
      last = now;
      setReload((n) => n + 1);
      clearTimeout(later);
      later = setTimeout(() => setReload((n) => n + 1), 4000);
    };
    const onMessage = (e: MessageEvent) => {
      if (e.origin === window.location.origin && e.data === PROGRESS_PING) again();
    };
    const onVisible = () => { if (document.visibilityState === 'visible') again(); };
    const onShow = (e: PageTransitionEvent) => { if (e.persisted) again(); };
    /*
     * **창을 오갈 때**(`focus`) 도 듣는다 — PC 에서 재생 창을 닫으면 이 창으로 초점이 돌아오는데,
     * 그때 `visibilitychange` 는 **울리지 않는다**(탭을 바꾸거나 최소화할 때만 운다). 그래서 따로 받는다.
     */
    window.addEventListener('message', onMessage);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onShow);
    window.addEventListener('focus', again);
    return () => {
      clearTimeout(later);
      window.removeEventListener('message', onMessage);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onShow);
      window.removeEventListener('focus', again);
    };
  }, []);

  const teachers = useMemo(() => {
    const m = new Map<number, string>();
    for (const l of data?.lectures ?? []) for (const x of l.teachers) if (x.name) m.set(x.id, x.name);
    return [...m.entries()];
  }, [data]);

  /** 거른 뒤의 구성 강좌 — 조건에 맞는 차시가 하나도 없는 강좌는 통째로 접는다 */
  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.lectures ?? [])
      .filter((l) => (selectedOnly ? l.bookmarked : true))
      .filter((l) => (teacher === '' ? true : l.teachers.some((x) => x.id === teacher)))
      .map((l) => ({
        ...l,
        curriculum: l.curriculum
          .filter((c) => (status === '' ? true : status === 'done' ? c.done : !c.done))
          .filter((c) => (!needle ? true : c.cname.toLowerCase().includes(needle))),
      }))
      .filter((l) => l.curriculum.length > 0 || (status === '' && !needle));
  }, [data, selectedOnly, teacher, status, q]);

  /** 열려 있던 것을 다시 누르면 접고, 다른 것을 누르면 그것만 남는다(앞엣것은 접히며 나간다) */
  const toggleOpen = (pid: number) => {
    const next = open === pid ? null : pid;
    if (open != null && open !== next) setClosing(open);
    setOpen(next);
  };

  /* 접힘이 끝나면 본문을 걷어 낸다 — CSS 애니메이션 길이(.24s)와 맞춘다 */
  useEffect(() => {
    if (closing == null) return;
    const id = setTimeout(() => setClosing(null), 260);
    return () => clearTimeout(id);
  }, [closing]);

  /** 구성 강좌 북마크 — 상품 번호로 서버에 남긴다(행이 없으면 서버가 만든다). 실패하면 되돌린다 */
  const toggleBookmark = async (l: ClassroomLecture) => {
    if (!data) return;
    const next = !l.bookmarked;
    const apply = (v: boolean) => (d: Classroom | null) =>
      d ? { ...d, lectures: d.lectures.map((x) => (x.productId === l.productId ? { ...x, bookmarked: v } : x)) } : d;
    setData(apply(next));
    const r = await fetch(`/api/members/me/enrollments/${(source as { cid: number }).cid}/lectures/${l.productId}/bookmark`, {
      method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ bookmark: next }),
    }).catch(() => null);
    if (!r?.ok) { setData(apply(!next)); toast(t('mc.mg.failed'), 'error'); }
  };

  const toggleFavorite = async () => {
    if (!data) return;
    const next = !data.favorite;
    setData({ ...data, favorite: next });
    const r = await fetch(source.kind === 'enrollment'
      ? `/api/members/me/enrollments/${source.cid}/favorite`
      : `/api/billing/classroom/${source.productId}/favorite`, {
      method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ favorite: next }),
    }).catch(() => null);
    if (!r?.ok) { setData((d) => (d ? { ...d, favorite: !next } : d)); toast(t('mc.mg.failed'), 'error'); }
  };

  const openCertificate = () => {
    if (!data) return;
    if (!data.certificateReady) { toast(t('mc.d.certNotReady', { n: data.product.certificateThreshold }), 'warning'); return; }
    window.open(`/my/courses/${(source as { cid: number }).cid}/certificate`, 'studyCert', 'width=860,height=1000,resizable=yes,scrollbars=yes');
  };

  const selectedOnlyClick = () => {
    if (selectedOnly) { setSelectedOnly(false); return; }
    if (!(data?.lectures ?? []).some((l) => l.bookmarked)) { toast(t('mc.d.noSelected'), 'warning'); return; }
    setSelectedOnly(true);
  };

  /** 아직 없는 화면(플레이어·강사 게시판 쓰기 등)으로 가는 자리 — 가는 척하지 않고 말한다(2026-09-07 사용자 지시) */
  const notReady = () => toast(t('mc.notReady'), 'info');

  /**
   * 최근 본 강의 — 가이드 `lec-list-item.active`. 마지막 재생 시각이 가장 늦은 차시 하나다.
   * [스트리밍]을 누르면 서버에 찍고(`/lessons/:ccid/recent`) 이 자리가 옮겨 간다 — 나의 현황의
   * 최근 본 강의도 같은 기록을 본다(2026-09-07 사용자 지시).
   */
  const recentCcid = useMemo(() => {
    let best: { ccid: number; at: number } | null = null;
    for (const l of data?.lectures ?? []) for (const c of l.curriculum) {
      if (!c.lastStudiedAt) continue;
      const at = new Date(c.lastStudiedAt).getTime();
      if (!best || at > best.at) best = { ccid: c.ccid, at };
    }
    return best?.ccid ?? null;
  }, [data]);
  const activeCcid = touchedCcid ?? recentCcid;
  /*
   * 플레이어를 제 창에 띄운다(2026-09-16 ⑩). 최근 본 강의 표시는 플레이어가 뜨면서 찍는다(수강권만 — 구독은
   * 재생 기록에서 읽는다). 구독은 계약 번호(bmid)와 **강의실 상품 번호**(upid)를 주소에 싣는다 — 진도 행이 그 둘로 묶인다.
   */
  const openLesson = (l: ClassroomLecture, ccid: number) => {
    setTouchedCcid(ccid);
    if (source.kind === 'enrollment') openPlayer(playerHref({ cid: source.cid }, l.productId, ccid), { pcApp, texts: shellTexts });
    /*
     * 구독은 진도 행을 **강의실 상품 번호**(`parentProductId`)로 묶는다 — 패키지든 단과든 마찬가지다
     * (`getBillingClassroom` 이 그 번호로 읽는다). 단과일 때 0 을 보냈더니 저장은 0, 조회는 상품 번호라
     * 진도가 통째로 사라져 보였다(2026-09-16 사용자 제보 "구독 1강 봤는데 진도율 누락").
     */
    else if (data?.bmid) openPlayer(playerHref({ bmid: data.bmid, upid: data.productId }, l.productId, ccid), { pcApp, texts: shellTexts });
    else notReady();
  };

  /*
   * 묶어 받기 바가 떠 있음을 몸통에 알린다 — **토스트를 그 위로 올리려고**(2026-09-10 사용자 제보
   * "토스트랑 겹친다"). 둘 다 화면 아래에 고정인데 토스트가 위(z 1000)라 바를 덮었다.
   * 쌓임 순서를 뒤집으면 토스트가 바 뒤로 숨으므로, 자리를 비켜 주는 쪽이 맞다.
   * 토스트는 셸이 그려 이 부품 밖에 있다 — 표를 몸통에 붙여야 닿는다.
   */
  useEffect(() => {
    document.body.classList.toggle('has-float-bar', checked.size > 0);
    return () => document.body.classList.remove('has-float-bar');
  }, [checked.size]);

  /*
   * 최근 수강한 차시(`lec-list-item.active`)를 **화면 위쪽 30% 자리로** 옮긴다(2026-09-10 사용자 지시).
   * 단과·패키지·패스 다 같다.
   *
   * **다 그려진 뒤에 옮긴다**(사용자 지시). 줄이 보이자마자 옮기면 그 뒤로 아코디언이 마저 펴지고
   * 남은 차시가 그려지면서 자리가 밀린다 — 문서 높이가 더 안 변할 때가 다 그려진 때다.
   * 들은 기록이 없으면(active 줄이 없으면) 움직이지 않는다.
   */
  /** 아코디언을 한 번이라도 펼쳤나 — 다시 읽기가 그 자리를 흔들지 않게 */
  const opened = useRef(false);
  const jumped = useRef(false);
  useEffect(() => {
    /*
     * **못 보는 기기면 옮기지 않는다**(2026-09-17 사용자 지시). 그 화면에는 재생 버튼이 없어
     * 옮겨 놓아도 할 일이 없고, 머리에 적어 둔 까닭(왜 버튼이 없는지)을 지나쳐 버린다.
     */
    if (!data || jumped.current || blockedDevice(data.platform, isMobile)) return;
    let raf = 0;
    let tries = 0;
    let lastH = -1;
    let still = 0;
    const tick = () => {
      const h = document.body.scrollHeight;
      if (h === lastH) still += 1; else { still = 0; lastH = h; }
      const el = document.querySelector('.lec-list-item.active');
      // 세 프레임 연속 높이가 같으면 다 그려진 것으로 본다(아코디언은 매 프레임 높이가 자란다)
      if (el && still >= 3) {
        jumped.current = true;
        window.scrollTo({ top: Math.max(0, el.getBoundingClientRect().top + window.scrollY - window.innerHeight * 0.3) });
        return;
      }
      if (++tries < 180) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [data, isMobile]);

  /*
   * 고른 차시는 **어느 강좌의 것인지까지** 기억한다(2026-09-17 로그 실측). 차시 번호만 담았더니 같은 차시가
   * 여러 구성 강좌에 걸린 패스에서 **한 번 골랐는데 여러 벌**이 실려 갔다 —
   * `items=1:29,1518:29,1518:139` (ccid 29 가 두 번). 고른 것을 `단과:차시` 로 적으면 그 헷갈림이 사라진다.
   */
  const key = (pid: number, ccid: number) => `${pid}:${ccid}`;
  const toggleCheck = (pid: number, ccid: number) => setChecked((s) => {
    const n = new Set(s); const k = key(pid, ccid);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  /**
   * [전체 선택]이 볼 **기준 강좌**(2026-09-17 사용자 지시 — "단과 기준으로 전체 선택되어야 한다.
   * 모든 단과가 전체 선택되면 안 된다"). 패키지·패스는 구성 강좌가 여럿인데 예전에는 그 전부를 골랐다.
   *
   * 기준은 **펼친 강좌**다 — 체크칸은 펼친 강좌 안에만 보이므로 손으로 고를 수 있는 것도 거기뿐이다.
   * 단과 상품은 강좌가 하나라 그것이 곧 기준이고, 펼친 것이 없으면(고르고 나서 접은 경우) 이미 고른 것이 속한 강좌를 쓴다 —
   * 그래야 눌러도 아무 일 없는 버튼이 되지 않는다.
   */
  const scopeGroup = useMemo(() => {
    const byOpen = groups.find((l) => l.productId === open);
    if (byOpen) return byOpen;
    if (groups.length === 1) return groups[0];
    return groups.find((l) => l.curriculum.some((c) => checked.has(key(l.productId, c.ccid)))) ?? null;
  }, [groups, open, checked]);
  const scopeKeys = useMemo(
    () => (scopeGroup?.curriculum ?? []).filter((c) => c.state === 'OPEN').map((c) => key(scopeGroup!.productId, c.ccid)),
    [scopeGroup],
  );
  const allChecked = scopeKeys.length > 0 && scopeKeys.every((k) => checked.has(k));
  /** 기준 강좌의 차시만 켜고 끈다 — 다른 강좌에서 고른 것은 건드리지 않는다 */
  const toggleAll = () => setChecked((s) => {
    const n = new Set(s);
    for (const k of scopeKeys) { if (allChecked) n.delete(k); else n.add(k); }
    return n;
  });
  /**
   * 묶어 받기로 고른 차시 — 자격 판정이 `pid` 로 가므로 단과와 짝지어 준다.
   * **차례는 화면에 보이는 순서**다(고른 순서가 아니라) — 앱의 받기 목록이 강의 차례대로 보여야 한다.
   */
  const checkedItems = useMemo(
    () => groups.flatMap((l) => l.curriculum
      .filter((c) => c.state === 'OPEN' && checked.has(key(l.productId, c.ccid)))
      .map((c) => ({ pid: l.productId, ccid: c.ccid }))),
    [groups, checked],
  );

  /**
   * 내려받기 — **모바일은 앱이 받는다**(2026-09-17 사용자 지시 "모바일 다운로드도 되게").
   * 스트리밍과 같은 길로 가되 `mode=download` 만 다르다 — 앱은 하나고 페이로드의 `action-type` 이 무엇을 할지 정한다.
   * 여럿을 고르면 한 번에 넘긴다(현행 `pop_multi_download`) — 앱 페이로드의 `content` 가 원래 배열이다.
   * PC 다운로더(현행 `pop_downloader`, 에이전트 설치형)는 아직이라 PC 에서는 준비 중이라고 말한다.
   */
  const download = (items: Array<{ pid: number; ccid: number }>) => {
    if (!items.length) return;
    if (!isMobileDevice()) { notReady(); return; }
    const opts = { mode: 'download' as const, items: items.length > 1 ? items : undefined };
    const first = items[0];
    if (source.kind === 'enrollment') openPlayer(playerHref({ cid: source.cid }, first.pid, first.ccid, opts), { panel: false });
    else if (data?.bmid) openPlayer(playerHref({ bmid: data.bmid, upid: data.productId }, first.pid, first.ccid, opts), { panel: false });
    else notReady();
  };

  if (error) {
    return (
      <div className="lec-header-box mb20">
        <p className="form-hint">{error}</p>
        {/* 구독으로 열었으면 여기는 강좌 상세 안이다 — 나의 강의실로 보내지 않는다 */}
        {canMark && (
          <div className="form-btns"><span /><SmartLink className="action-btn action-cancel" href="/my">{t('rv.toList')}</SmartLink></div>
        )}
      </div>
    );
  }
  if (!data) return <Working block message={t('common.loading')} />;

  /*
   * 단과 vs 패키지·패스 — 가이드 `myclass_info.html` 의 "그룹 3" 이 단과다(2026-09-07 사용자 지적).
   * 단과는 구성 강좌가 자기 하나라 **그룹 머리(제목·진도·접기·북마크)를 그리지 않고** 정보행·차시 표만
   * 바로 놓는다. 패키지·패스는 강좌마다 머리가 있는 아코디언이다.
   */
  /**
   * 못 보는 기기로 들어왔는가 — 값이 있으면 그 강좌가 **어느 쪽 전용**인지다(2026-09-17 사용자 확정).
   * 단가를 PC·모바일로 나눠 파는 마켓에서는 산 단가가 곧 수강 가능 기기고, 재생도 같은 값으로 막는다.
   * 여기서는 **[스트리밍]·[다운로드]와 체크칸을 아예 감추고** 까닭은 강좌 머리에 한 줄로 한 번만 적는다 —
   * 눌러 봐야 막히는 버튼을 두지 않고, 차시마다 같은 문장을 되풀이하지도 않는다.
   */
  const blockedBy = blockedDevice(data.platform, isMobile);
  const single = data.productType === 'LECTURE';
  /* 질문이 닿을 곳이 하나라도 있나 — 강사 Q&A 든 1:1 문의든. 없으면 버튼을 그리지 않는다 */
  const askableAnywhere = data.helpBoardId != null
    || data.lectures.some((l) => l.teachers.some((x) => x.qnaBoardId != null));
  const today = hoursMinutes(data.stats.todaySeconds);
  const total = hoursMinutes(data.stats.totalSeconds);
  /*
   * 수강 제한(구매 시점 스냅샷)을 통계 칸에 곁들인다(2026-09-07 사용자 지시 — 가이드에 없어 여기서 정했다).
   * 문구는 상품 목록·상세가 쓰는 것과 **같은 함수**(`limitParts`)로 만든다 — 자리마다 다르게 말하면 안 된다.
   *  · 오늘 본 차시   ← 1일 제한(차시 수·강좌 수)
   *  · 오늘 재생시간  ← 차시별 제한(배수·고정 시간)
   *  · 전체 재생시간  ← 누적(강좌 전체) 제한 — 고정 시간이면 `현재 / 한도` 로 함께 적는다
   */
  const limits = limitParts({ studyLimit: data.studyLimit ?? {} } as Parameters<typeof limitParts>[0], t);
  const lectureCap = data.studyLimit?.lecture?.mode === 'FIXED' ? hoursMinutes(data.studyLimit.lecture.seconds ?? 0) : null;
  const paths = data.product.categoryPaths;
  const timeCell = (v: { h: number; m: number }) => (
    <>
      {v.h > 0 && <>{v.h}<span className="lec-stat-unit">{t('mc.d.hour')}</span> </>}
      {String(v.m).padStart(v.h > 0 ? 2 : 1, '0')}<span className="lec-stat-unit">{t('mc.d.min')}</span>
    </>
  );

  return (
    <>
      {/* ── 강좌 헤더 ── */}
      <div className="lec-header-box mb20">
        <div className="lec-header-top">
          <div className="d-flex jc-end mb12">
            <span className="fs12">{t(STATUS_KEY[data.status] ?? 'mc.inProgress')} · {t('mc.d.total', { n: data.lessonCount })}</span>
          </div>
          <div>
            {(catsOpen ? paths : paths.slice(0, 1)).map((path, pi) => (
              <div className="lecture-depth" key={path[path.length - 1]?.id ?? pi}>
                {pi === 0 && (
                  <span className={`badge ${badgeClass(data.productType)} mb8 mr5`} style={{ display: 'inline-block' }}>{typeLabel(data.productType, t)}</span>
                )}
                {path.map((c, i) => (
                  <span key={c.id}>
                    {i > 0 && <span className="depth-sep">›</span>}
                    <span className={i === path.length - 1 ? 'depth-current' : undefined}>{pickName(c.name, lang)}</span>
                  </span>
                ))}
                {/* 분류가 여럿이면 첫 줄 끝에 펼침 버튼 — 다 펼친 뒤에는 마지막 줄 끝에 접기 */}
                {paths.length > 1 && pi === (catsOpen ? paths.length - 1 : 0) && (
                  <button type="button" className="lec-cats-toggle" onClick={() => setCatsOpen((v) => !v)} aria-expanded={catsOpen}>
                    {catsOpen ? t('common.collapse') : t('mc.d.moreCats', { n: paths.length - 1 })}
                    <Icon name={catsOpen ? ICON_CHEVRON_UP : ICON_CHEVRON} size={14} />
                  </button>
                )}
              </div>
            ))}
            {paths.length === 0 && (
              <div className="lecture-depth">
                <span className={`badge ${badgeClass(data.productType)} mb8 mr5`} style={{ display: 'inline-block' }}>{typeLabel(data.productType, t)}</span>
              </div>
            )}
            <h2 className="lec-title">{data.product.name}</h2>

            {/*
              못 보는 기기로 들어왔을 때 — **여기 한 줄로 한 번만** 말한다(2026-09-17 사용자 확정).
              차시마다 같은 문장을 붙이면 수십 줄이 같은 말을 되풀이한다. 아래 차시 줄에서는 버튼과 체크칸이 빠지는데,
              왜 빠졌는지는 이 줄이 설명한다.
            */}
            {blockedBy && (
              <p className="lec-device-note">
                <Icon name={ICON_INFO} size={15} />
                {t(blockedBy === 'PC' ? 'mc.d.pcOnly' : 'mc.d.mobileOnly')}
              </p>
            )}

            <div className="lec-stats-grid">
              <div className="lec-stat-card">
                <div className="lec-stat-icon"><Icon name={ICON_PLAY_CIRCLE} /></div>
                <div className="lec-stat-body">
                  <p className="lec-stat-label">{t('mc.d.todayLessons')}</p>
                  <p className="lec-stat-val">
                    {data.stats.todayLessons}
                    {data.studyLimit?.daily?.lessons ? <span className="lec-stat-unit"> / {data.studyLimit.daily.lessons}</span> : null}
                    <span className="lec-stat-unit">{t('mc.d.lessonsUnit')}</span>
                  </p>
                  {limits.daily.length > 0 && <p className="lec-stat-limit">{limits.daily.map((x) => x.long).join(' · ')}</p>}
                </div>
              </div>
              <div className="lec-stat-card">
                <div className="lec-stat-icon"><Icon name={ICON_CLOCK} /></div>
                <div className="lec-stat-body">
                  <p className="lec-stat-label">{t('mc.d.todayTime')}</p>
                  <p className="lec-stat-val">{timeCell(today)}</p>
                  {limits.lesson && <p className="lec-stat-limit">{limits.lesson.long}</p>}
                </div>
              </div>
              <div className="lec-stat-card">
                <div className="lec-stat-icon"><Icon name={ICON_TIMER} /></div>
                <div className="lec-stat-body">
                  <p className="lec-stat-label">{t('mc.d.totalTime')}</p>
                  <p className="lec-stat-val">
                    {timeCell(total)}
                    {lectureCap && <span className="lec-stat-unit"> / {lectureCap.h}{t('mc.d.hour')}{lectureCap.m ? ` ${lectureCap.m}${t('mc.d.min')}` : ''}</span>}
                  </p>
                  {limits.lecture && <p className="lec-stat-limit">{limits.lecture.long}</p>}
                </div>
              </div>
              <div className="lec-stat-card lec-stat-progress">
                <div className="lec-stat-progress-top">
                  <div className="lec-stat-icon accent"><Icon name={ICON_CHART} /></div>
                  <div className="lec-stat-body">
                    <p className="lec-stat-label">{t('mc.d.progress')}</p>
                    <p className="lec-stat-val accent">{data.totalProgress}<span className="lec-stat-unit">%</span></p>
                  </div>
                </div>
                <div className="lec-stat-bar-wrap">
                  <div className="lec-stat-bar"><div className="lec-stat-bar-fill" style={{ width: `${Math.min(100, data.totalProgress)}%` }}></div></div>
                </div>
              </div>
            </div>
            {/* 강사명 줄(가이드 lec-teacher-txt)은 뺐다 — 구성 강좌 정보행에 이미 있다(2026-09-07 사용자 지시) */}
            {data.product.profile && <div className="lec-txt"><p>{data.product.profile}</p></div>}
          </div>
        </div>
        {/* 수강확인서는 수강권(주문)이 근거라 구독에는 없다. 즐겨찾기는 구독도 쓴다(별도 표) */}
        <div className="lec-act-btns mt15">
          <button className={`action-btn badge badge-light btn-sm${data.favorite ? ' active' : ''}`} onClick={() => void toggleFavorite()} aria-pressed={data.favorite}>
            <Icon name={ICON_STAR} /> {t('mc.tab.fav')}
          </button>
          {/*
            질문하기 — **주 입구**(2026-09-10). 아코디언을 펼치지 않아도 늘 보인다.
            갈 곳이 하나도 없는 마켓(강사 게시판도 1:1 문의도 없음)에서만 감춘다.
          */}
          {askableAnywhere && (
            /* 지금 펼쳐 둔 강좌를 실어 보낸다 — 레이어가 그것을 기본으로 고른다(2026-09-10).
               단과는 구성 강좌가 하나뿐이라 늘 그것이다 */
            <button className="action-btn badge badge-light btn-sm"
              onClick={() => setAsking({ lecture: data.lectures.find((l) => (single ? true : l.productId === open)) })}>
              <Icon name={ICON_MSG} /> {t('mc.ask.title')}
            </button>
          )}
          {data.product.reviewEnabled && (
            /* 취소하면 **이 화면으로 돌아온다**(2026-09-11 사용자 지적) — 구독으로 열었으면 강좌 상세다 */
            <SmartLink className="action-btn badge badge-light btn-sm"
              href={`/boards/review/write?from=${encodeURIComponent(source.kind === 'enrollment' ? `/my/courses/${source.cid}` : `/lectures/${source.productId}`)}`}>
              <Icon name={ICON_PEN} /> {t('mc.d.review')}
            </SmartLink>
          )}
          {canMark && (
            <button className="action-btn badge badge-light btn-sm" onClick={openCertificate}><Icon name={ICON_FILE} /> {t('mc.d.cert')}</button>
          )}
        </div>
      </div>

      {/* ── 필터 + 검색 — 단과에는 없다(강좌가 하나라 고를 것이 없다, 2026-09-07 사용자 지시) ── */}
      {!single && (
      <div className="lec-toolbar mb15">
        <div className="lec-toolbar-filters">
          {teachers.length > 0 && (
            <select className="form-select lec-filter-select" value={teacher} onChange={(e) => setTeacher(e.target.value ? Number(e.target.value) : '')}>
              <option value="">{t('mc.d.teacherAll')}</option>
              {teachers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          )}
          <select className="form-select lec-filter-select" value={status} onChange={(e) => setStatus(e.target.value as '' | 'done' | 'todo')}>
            <option value="">{t('mc.d.status')}</option>
            <option value="done">{t('mc.d.done')}</option>
            <option value="todo">{t('mc.d.notDone')}</option>
          </select>
        </div>
        <div className="bbs-search-input-wrap lec-search-wrap">
          <Icon name={ICON_SEARCH} />
          <input type="text" className="bbs-search-input" placeholder={t('mc.d.searchPh')} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      )}

      {/* ── 섹션 헤더 ── */}
      <div className="lec-sec-header mb10">
        <div className="d-flex ai-center gap6">
          <span className="lec-sec-bar"></span>
          <span className="fs14 fw-bold ml5" style={{ color: 'var(--text1)' }}>{t('mc.d.lectures')}</span>
        </div>
        {/* [전체 보기]/[선택 강좌만 보기] 는 표시를 근거로 한다 — 표시할 수 없으면 고를 것도 없다 */}
        {!single && canMark && (
          <div className="d-flex gap6">
            <button className="action-btn action-cancel btn-sm mr5" onClick={() => setSelectedOnly(false)}><Icon name={ICON_GRID} /> {t('mc.viewAll')}</button>
            <button className={`action-btn action-cancel btn-sm${selectedOnly ? ' btn-view-active' : ''}`} onClick={selectedOnlyClick}>
              <Icon name={ICON_BOOKMARK} /> {t('mc.favOnly')}
            </button>
          </div>
        )}
      </div>

      {/* ── 강의 목록 ── */}
      <div className="lec-list-box">
        {groups.length === 0 && <p className="form-hint py20">{t('mc.d.noLesson')}</p>}
        {groups.map((l) => {
          /*
           * 오픈일이 안 된 구성 강좌는 **펼칠 수 없다**(2026-09-07 사용자 지시) — 머리만 보이고 누르면 오픈일을
           * 말한다. 오픈일이 지나면 서버가 OPEN 으로 바꿔 주고 그때부터 펼쳐진다. 마감된 강좌는 펼쳐진다(이미
           * 본 기록을 볼 수 있어야 한다).
           */
          const locked = l.state === 'SCHEDULED';
          const isOpen = !locked && (single || open === l.productId);
          /* 접히는 중 — 다시 열렸으면(`isOpen`) 접힘은 취소된 것이다 */
          const isClosing = !isOpen && closing === l.productId;
          return (
            <div className="lec-group" key={l.productId} data-state={l.progress >= 100 ? 'done' : 'ing'}>
              {/* 단과는 머리(제목·진도·접기·북마크)가 없다 — 가이드 "그룹 3". 강좌가 하나라 머리 상자가 이미 그 역할이다 */}
              {!single && (
                <div className="lec-group-header-wrap">
                  {/* 표시(북마크)는 `tb_class_detail` 에 남긴다 — 구독에는 그 행이 없다 */}
                  {canMark && (
                    <button className={`bm-btn bm-btn--group${l.bookmarked ? ' on' : ''}`} onClick={() => void toggleBookmark(l)} title={t('mc.favOnly')} aria-pressed={l.bookmarked}>
                      <Icon name={ICON_BOOKMARK} />
                    </button>
                  )}
                  <button className={`lec-group-header${isOpen ? ' open' : ''}${locked ? ' is-locked' : ''}`} aria-expanded={isOpen} aria-disabled={locked}
                    onClick={() => (locked ? toast(t('mc.d.notOpenYet', { d: fmtDateDot(l.opensAt) }), 'info') : toggleOpen(l.productId))}>
                    <div className="lec-group-info">
                      <span className="lec-group-title">{l.name}</span>
                      <span className="lec-group-meta">
                        {[l.teachers.map((x) => x.name).filter(Boolean).join(', '), t('mc.d.total', { n: l.lessonCount })].filter(Boolean).join(' · ')}
                        {/* 구성 강좌 자체의 오픈 예정·마감(§5.5 자동 오픈/종료) */}
                        {l.state === 'SCHEDULED' && <> · <span className="badge badge-light">{t('mc.d.opensOn', { d: fmtDateDot(l.opensAt) })}</span></>}
                        {l.state === 'CLOSED' && <> · <span className="badge badge-dark">{t('mc.d.closed')}</span></>}
                      </span>
                    </div>
                    <div className="lec-group-right">
                      <div className="lec-group-prog-wrap">
                        <div className="lec-group-bar"><div className="lec-group-bar-fill" style={{ width: `${Math.min(100, l.progress)}%` }}></div></div>
                        <span className="lec-group-pct">{l.progress}%</span>
                      </div>
                      <Icon name={ICON_CHEVRON} className="lec-group-icon" />
                    </div>
                  </button>
                </div>
              )}
              {(isOpen || isClosing) && (
                /*
                 * 펼침·접힘 애니메이션 — 감싸개가 하나 더 있는 이유(2026-09-10 사용자 지시).
                 * 높이를 모르는 내용은 `max-height` 로 못 편다(차시가 3개일 때와 300개일 때
                 * 속도가 딴판이 된다). `grid-template-rows: 0fr → 1fr` 은 실제 높이만큼만 펴는데,
                 * 그러려면 **본문이 한 칸(트랙 하나)** 이어야 한다. 그래서 안쪽을 묶는다.
                 */
                <div className={`lec-group-body${isClosing ? ' is-closing' : ' is-open'}`}>
                  <div className="lec-group-body-inner">
                    {/*
                      정보행(강사·교재·공지·Q&A)은 통째로 뺐다(2026-09-10 사용자 지시).
                      강사명은 그룹 머리(`lec-group-meta`)에 이미 있고, [질문하기] 는 머리 상자에 있다.
                    */}
                    <div className="lec-list-head">
                      <div className="lh-chk"></div>
                      <div className="lh-no">{t('mc.d.order')}</div>
                      <div className="lh-name">{t('mc.d.name')}</div>
                      <div className="lh-note">{t('mc.d.note')}</div>
                      <div className="lh-play">{t('mc.d.play')}</div>
                    </div>
                    {l.curriculum.map((c) => (
                      <div className={`lec-list-item${c.ccid === activeCcid ? ' active' : ''}`} key={c.ccid}>
                        <div className="li-chk">
                          {/* 아직 안 열린(또는 마감된) 차시는 묶어 받기에도 고를 수 없다. 못 보는 기기에서도 고를 것이 없다 */}
                          {c.state === 'OPEN' && !blockedBy && (
                            <label className="custom-check">
                              <input type="checkbox" className="lec-chk" checked={checked.has(key(l.productId, c.ccid))} onChange={() => toggleCheck(l.productId, c.ccid)} />
                              <span className="check-box"></span>
                            </label>
                          )}
                        </div>
                        <div className="li-no"><span className="lec-no">{t('mc.d.lessonNo', { n: c.cnum ?? '' })}</span></div>
                        <div className="li-name">
                          <p className="lec-cls-name">
                            {c.cname}
                            {c.hasSubtitle && <> <span className="badge badge-dark">CC</span></>}
                            {/* 맛보기 배지는 뺐다(2026-09-16 사용자 지시) — 수강 중인 회원에게 '맛보기' 는 뜻이 없다 */}
                          </p>
                          {/* 차시 게이지는 늘 강조색(가이드 myclass_info 는 45%·66% 도 rate-fill-high). 빨강(low)은 모바일 메뉴의
                              **강좌 단위** 경고 색이라 방금 시작한 차시가 빨갛게 보이면 안 된다(2026-09-07 사용자 지적) */}
                          <div className="rate-bar-bg mb3"><div className="rate-bar-fill rate-fill-high" style={{ width: `${Math.min(100, c.progress)}%` }}></div></div>
                          {/* 왼쪽 = 내가 본 누적 재생시간(진도율의 분자, 러닝타임에서 캡) · 오른쪽 = 총 재생시간(2026-09-07 사용자 확정).
                              마지막 재생 위치를 적으면 게이지와 다른 값이 보인다 — 게이지와 숫자는 같은 근거여야 한다 */}
                        <div className="d-flex jc-between">
                          <span className="fs12">{clock(Math.min(c.playbackDuration, c.rtime ?? c.playbackDuration))}</span>
                          <span className="fs12">{clock(c.rtime)}</span>
                        </div>
                        </div>
                        <div className="li-note">
                          {c.file
                            ? <a className="action-btn action-cancel btn-sm" href={c.file} target="_blank" rel="noreferrer" title={t('mc.d.note')}><Icon name={ICON_FILE} /></a>
                            : <span className="fs12">-</span>}
                        </div>
                        <div className="li-play">
                          {/*
                           * 오픈 예정·마감 차시는 버튼 대신 상태를 말한다(2026-09-07 사용자 지적 "차시 오픈일 설정이
                           * 적용 안 됐다") — 판정은 서버(`state`)가 했다. 오픈일이 오면 서버가 OPEN 으로 바꿔 준다.
                           */}
                          {c.state === 'SCHEDULED' && (
                            <span className="badge badge-light">{t('mc.d.opensOn', { d: fmtDateDot(c.opensAt ?? l.opensAt) })}</span>
                          )}
                          {c.state === 'CLOSED' && <span className="badge badge-dark">{t('mc.d.closed')}</span>}
                          {/* 못 보는 기기에서는 버튼을 그리지 않는다 — 까닭은 강좌 머리에 한 줄로 적었다 */}
                          {c.state === 'OPEN' && !blockedBy && (
                            <div className="d-flex gap5 mb5">
                              <button type="button" className="action-btn action-cancel btn-sm" onClick={() => openLesson(l, c.ccid)}><Icon name={ICON_PLAY} /> {t('mc.d.stream')}</button>
                              {/* 모바일은 앱이 받는다. PC 다운로더(에이전트 설치형)는 아직이라 준비 중이라고 말한다 */}
                              <button type="button" className="action-btn action-submit btn-sm" onClick={() => download([{ pid: l.productId, ccid: c.ccid }])}><Icon name={ICON_DOWNLOAD} /> {t('mc.d.download')}</button>
                            </div>
                          )}
                          {/*
                            차시별 재생 제한 — **'최근 수강' 이 쓰던 자리, 같은 모양**(2026-09-10
                            사용자 지시 "최근 수강처럼 텍스트만"). 줄을 더 두면 차시마다 키가
                            커지고, 시간 줄 옆에 붙이면 숫자가 셋 나란해 읽히지 않는다.
                            최근 본 차시는 줄 강조(`active`)가 이미 말하므로 그 날짜는 걷었다.
                            **본 시간은 적지 않는다** — 게이지 아래 왼쪽 숫자가 이미 그 값이다
                            (2026-09-10 사용자 지적). 여기 적을 것은 한도와 남은 시간이다.
                          */}
                          {c.limit && (
                            <span className={`fs12 lec-limit${c.limit.used >= c.limit.cap ? ' lec-limit-over' : ''}`}>
                              {c.limit.used >= c.limit.cap
                                ? t('mc.d.limitOver', { cap: clock(c.limit.cap) })
                                : t('mc.d.limitLeft', { cap: clock(c.limit.cap), left: clock(c.limit.cap - c.limit.used) })}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {asking && <AskModal data={data} lecture={asking.lecture} onClose={() => setAsking(null)} />}

      {/* 묶어 받기 바 — 고른 것이 있을 때만(가이드 lec-float-bar). 다운로드는 플레이어 단계에서 이어진다 */}
      <div className={`lec-float-bar${checked.size > 0 ? ' show' : ''}`}>
        <span className="lec-float-cnt"><strong>{checked.size}</strong>{t('mc.d.selected')}</span>
        <div className="d-flex gap8">
          {/* [전체 선택] 은 가이드가 알약(`tag-item`)으로 바꿨다(2026-09-10 디자이너) — 취소 버튼이 아니다 */}
          <button className="action-btn tag-item btn-sm" onClick={toggleAll}>
            <Icon name={ICON_CHECK_SQ} /> {allChecked ? t('mc.d.deselectAll') : t('mc.d.selectAll')}
          </button>
          {/*
            **버튼 사이 공백**(2026-09-11 사용자 지적 "버튼이 딱 붙어 있다").
            가이드는 이 묶음을 `display: block; text-align: center` 로 두므로 버튼이 inline 이고,
            HTML 의 줄바꿈 공백이 그대로 간격이 된다. JSX 는 줄 사이 공백을 지우므로 없으면 붙는다 —
            `gap` 을 되살리면 디자이너가 짠 배치를 다시 덮게 되니 가이드 DOM 과 같은 것을 넣는다.
          */}
          {' '}
          <button className="action-btn action-submit btn-sm" onClick={() => download(checkedItems)}>
            <Icon name={ICON_DOWNLOAD} /> {t('mc.d.download')}
          </button>
        </div>
      </div>
    </>
  );
}
